import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const categorySlugs = ['all', 'fantasy', 'xianxia', 'urban', 'scifi', 'history', 'romance', 'comedy'];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (slug && !categorySlugs.includes(slug)) {
      navigate('/category/all', { replace: true });
    }
  }, [slug, navigate]);

  const filteredComics = activeSlug === 'all'
    ? comics
    : comics.filter((c) => c.category === activeSlug);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    navigate(`/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部漫画'} - 073漫画网`,
    description: `浏览073漫画网${currentCategory?.name || '全部'}漫画分类，发现更多精彩漫画作品。`,
    url: `https://lninghy.cn/category/${activeSlug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {currentCategory?.name || '全部漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">{currentCategory?.description || '浏览全部漫画作品'}</p>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={comic.cover}
                        alt={`${comic.title}漫画封面`}
                        title={`${comic.title} - 073漫画网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[10px] font-medium">
                          {comic.rating}分
                        </span>
                      </div>
                      {comic.status === '连载中' && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-medium">
                            连载中
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      {comic.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}